/**
 * BuildConfigurationDisplay Component
 *
 * Purpose: Display build configuration as the PRIMARY UI focus.
 * This component shows the deterministic installation plan generated by the backend.
 *
 * Design Principles:
 * - Visual prominence: Large, clear display with hierarchy
 * - Glassmorphism: Consistent with current chat UI design
 * - Progressive disclosure: Expandable sections for details
 * - Read-only: This is a display component, not an editor
 *
 * Content:
 * 1. Installation Order (numbered list with dependencies)
 * 2. Server Requirements (CPU, RAM, Storage with visual indicators)
 * 3. Deployment Preview (code blocks for docker-compose, odoo.conf)
 * 4. Estimated Duration & Cost
 */

import type React from "react";
import { useState } from "react";

interface InstallationConfiguration {
  sessionId: string;
  timestamp: Date;
  modules: ModuleInstallationItem[];
  installationOrder: string[];
  serverRequirements: {
    cpu: number;
    ram: number;
    storage: number;
    dropletSize: string;
    region: string;
  };
  deploymentScript: string;
  configFiles: {
    odooConf: string;
    dockerCompose: string;
    nginxConf: string;
  };
  estimatedDuration: number;
  estimatedCost: number;
}

interface ModuleInstallationItem {
  technicalName: string;
  name: string;
  version: string;
  dependencies: string[];
  installationStep: number;
  estimatedInstallTime: number;
}

interface BuildConfigurationDisplayProps {
  configuration: InstallationConfiguration;
}

export const BuildConfigurationDisplay: React.FC<BuildConfigurationDisplayProps> = ({
  configuration,
}) => {
  const [showDeploymentScript, setShowDeploymentScript] = useState(false);
  const [showConfigFiles, setShowConfigFiles] = useState(false);

  // Format duration from seconds to human readable
  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Build Configuration</h2>
        <p className="text-gray-600 text-lg">Your deterministic installation plan is ready</p>
      </div>

      {/* Server Requirements - Prominent Display */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border-2 border-blue-200">
        <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <span className="text-2xl mr-2">🖥️</span>
          Server Requirements
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 text-center shadow-sm">
            <div className="text-3xl font-bold text-blue-600 mb-1">
              {configuration.serverRequirements.cpu}
            </div>
            <div className="text-sm text-gray-600">CPU Cores</div>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm">
            <div className="text-3xl font-bold text-green-600 mb-1">
              {Math.round(configuration.serverRequirements.ram / 1024)}GB
            </div>
            <div className="text-sm text-gray-600">RAM</div>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm">
            <div className="text-3xl font-bold text-purple-600 mb-1">
              {Math.round(configuration.serverRequirements.storage / 1024)}GB
            </div>
            <div className="text-sm text-gray-600">Storage</div>
          </div>
        </div>
        <div className="mt-4 text-center text-sm text-gray-600">
          <span className="font-medium">Droplet:</span>{" "}
          {configuration.serverRequirements.dropletSize} •{" "}
          <span className="font-medium">Region:</span> {configuration.serverRequirements.region}
        </div>
      </div>

      {/* Installation Order */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
        <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <span className="text-2xl mr-2">📦</span>
          Installation Order ({configuration.modules.length} modules)
        </h3>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {configuration.modules.map((module, index) => (
            <div
              key={module.technicalName}
              className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="w-8 h-8 bg-gray-900 text-white text-sm font-bold rounded-full flex items-center justify-center flex-shrink-0">
                {index + 1}
              </div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900">{module.name}</div>
                <div className="text-sm text-gray-500">
                  <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                    {module.technicalName}
                  </span>
                  {module.dependencies.length > 0 && (
                    <span className="ml-2">
                      • Depends on: {module.dependencies.slice(0, 3).join(", ")}
                      {module.dependencies.length > 3 && ` +${module.dependencies.length - 3} more`}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-xs text-gray-400">~{module.estimatedInstallTime}s</div>
            </div>
          ))}
        </div>
      </div>

      {/* Estimates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
          <div className="text-2xl font-bold text-blue-600 mb-1">
            {formatDuration(configuration.estimatedDuration)}
          </div>
          <div className="text-sm text-gray-600">Estimated Installation Time</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
          <div className="text-2xl font-bold text-green-600 mb-1">
            ${configuration.estimatedCost.toFixed(2)}/mo
          </div>
          <div className="text-sm text-gray-600">Estimated Monthly Cost</div>
        </div>
      </div>

      {/* Deployment Script (Expandable) */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <button
          type="button"
          onClick={() => setShowDeploymentScript(!showDeploymentScript)}
          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <span className="font-semibold text-gray-900 flex items-center">
            <span className="text-xl mr-2">🚀</span>
            Deployment Script
          </span>
          <svg
            className={`w-5 h-5 transform transition-transform ${showDeploymentScript ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {showDeploymentScript && (
          <div className="px-6 pb-4">
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
              {configuration.deploymentScript ||
                "# Deployment script will be generated during execution"}
            </pre>
          </div>
        )}
      </div>

      {/* Configuration Files (Expandable) */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <button
          type="button"
          onClick={() => setShowConfigFiles(!showConfigFiles)}
          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <span className="font-semibold text-gray-900 flex items-center">
            <span className="text-xl mr-2">⚙️</span>
            Configuration Files
          </span>
          <svg
            className={`w-5 h-5 transform transition-transform ${showConfigFiles ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {showConfigFiles && (
          <div className="px-6 pb-4 space-y-4">
            {/* Docker Compose */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">docker-compose.yml</h4>
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-xs">
                {configuration.configFiles.dockerCompose || "# Configuration will be generated"}
              </pre>
            </div>
            {/* Odoo Config */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">odoo.conf</h4>
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-xs">
                {configuration.configFiles.odooConf || "# Configuration will be generated"}
              </pre>
            </div>
            {/* Nginx Config */}
            {configuration.configFiles.nginxConf && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">nginx.conf</h4>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-xs">
                  {configuration.configFiles.nginxConf}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
